import { useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useCallback } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import { DEFAULT_EMOJI_NAMES, getEmojiItemByChar } from '../utils/emoji';

const storageKeys = {
  recentlyUsedEmojis: 'slabRecentlyUsedEmojis',
  recentlyMentionedUserIds: 'slabRecentlyMentionedUserIds',
  recentlyUsedTemplateIds: 'slabRecentlyUsedTemplateIds',
  recentlyViewedPostIds: 'slabRecentlyViewedPostIds',
} as const;

const recentlyUsedEmojisAtom = atomWithStorage<string[] | null>(
  storageKeys.recentlyUsedEmojis,
  null,
);

const recentlyMentionedUserIdsAtom = atomWithStorage<string[] | null>(
  storageKeys.recentlyMentionedUserIds,
  null,
);

const recentlyUsedTemplateIdsAtom = atomWithStorage<string[] | null>(
  storageKeys.recentlyUsedTemplateIds,
  null,
);

const recentlyViewedPostIdsAtom = atomWithStorage<string[] | null>(
  storageKeys.recentlyViewedPostIds,
  null,
);

const EMPTY_ARRAY: string[] = [];

const uniqWithLimit = <T>(list: T[], maxLength: number) => {
  return Array.from(new Set(list)).slice(0, maxLength);
};

export const useRecentlyUsedEmojis = () => {
  const value = useAtomValue(recentlyUsedEmojisAtom);
  const valueToMigrate = useAppSelector((state) => state.ui.recentlyUsedEmojis);
  return value ?? valueToMigrate ?? DEFAULT_EMOJI_NAMES;
};

export const useAddRecentlyUsedEmoji = () => {
  const HISTORY_MAX_LENGTH = 100;

  const setEmojis = useSetAtom(recentlyUsedEmojisAtom);
  const valueToMigrate = useAppSelector((state) => state.ui.recentlyUsedEmojis);

  return useCallback(
    (char: string) => {
      const emoji = getEmojiItemByChar(char);
      if (emoji) {
        setEmojis((value) =>
          uniqWithLimit(
            [emoji.name, ...(value ?? valueToMigrate ?? DEFAULT_EMOJI_NAMES)],
            HISTORY_MAX_LENGTH,
          ),
        );
      }
    },
    [setEmojis, valueToMigrate],
  );
};

export const useRecentlyMentionedUserIds = () => {
  const value = useAtomValue(recentlyMentionedUserIdsAtom);
  const valueToMigrate = useAppSelector(
    (state) => state.ui.recentlyMentionedUserIds,
  );
  return value ?? valueToMigrate ?? EMPTY_ARRAY;
};

export const useAddRecentlyMentionedUserId = () => {
  const HISTORY_MAX_LENGTH = 25;

  const setRecentlyMentionedUserIds = useSetAtom(recentlyMentionedUserIdsAtom);
  const valueToMigrate = useAppSelector(
    (state) => state.ui.recentlyMentionedUserIds,
  );

  return useCallback(
    (userId: string) => {
      setRecentlyMentionedUserIds((value) =>
        uniqWithLimit(
          [userId, ...(value ?? valueToMigrate ?? EMPTY_ARRAY)],
          HISTORY_MAX_LENGTH,
        ),
      );
    },
    [setRecentlyMentionedUserIds, valueToMigrate],
  );
};

export const useRecentlyUsedTemplateIds = () => {
  const value = useAtomValue(recentlyUsedTemplateIdsAtom);
  const valueToMigrate = useAppSelector(
    (state) => state.ui.recentlyUsedTemplateIds,
  );
  return value ?? valueToMigrate ?? EMPTY_ARRAY;
};

export const useAddRecentlyUsedTemplateId = () => {
  const HISTORY_MAX_LENGTH = 10;

  const setRecentlyMentionedUserIds = useSetAtom(recentlyUsedTemplateIdsAtom);
  const valueToMigrate = useAppSelector(
    (state) => state.ui.recentlyUsedTemplateIds,
  );

  return useCallback(
    (templateId: string) => {
      setRecentlyMentionedUserIds((value) =>
        uniqWithLimit(
          [templateId, ...(value ?? valueToMigrate ?? EMPTY_ARRAY)],
          HISTORY_MAX_LENGTH,
        ),
      );
    },
    [setRecentlyMentionedUserIds, valueToMigrate],
  );
};

export const useRecentlyViewedPostIds = () => {
  const value = useAtomValue(recentlyViewedPostIdsAtom);
  const valueToMigrate = useAppSelector(
    (state) => state.ui.recentlyViewedPostIds,
  );
  return value ?? valueToMigrate ?? EMPTY_ARRAY;
};

export const useAddRecentlyViewedPostIds = () => {
  const HISTORY_MAX_LENGTH = 25;
  const setRecentlyViewedPostIds = useSetAtom(recentlyViewedPostIdsAtom);
  const valueToMigrate = useAppSelector(
    (state) => state.ui.recentlyViewedPostIds,
  );

  return useCallback(
    (postIds: string[]) => {
      setRecentlyViewedPostIds((value) =>
        uniqWithLimit(
          [...postIds, ...(value ?? valueToMigrate ?? EMPTY_ARRAY)],
          HISTORY_MAX_LENGTH,
        ),
      );
    },
    [setRecentlyViewedPostIds, valueToMigrate],
  );
};

export const useRemoveFromRecentlyViewedPostIds = () => {
  const setRecentlyViewedPostIds = useSetAtom(recentlyViewedPostIdsAtom);
  const valueToMigrate = useAppSelector(
    (state) => state.ui.recentlyViewedPostIds,
  );

  return useCallback(
    (postId: string) => {
      setRecentlyViewedPostIds((value) =>
        (value ?? valueToMigrate ?? EMPTY_ARRAY).filter((id) => id !== postId),
      );
    },
    [setRecentlyViewedPostIds, valueToMigrate],
  );
};

export const purgeUIPersistedState = () => {
  for (const key of Object.values(storageKeys)) {
    localStorage.removeItem(key);
  }
};
